import { differenceInDays } from 'date-fns';

import { expVal } from '@confluence/feature-experiments';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';

const FIRST_TWO_WEEKS_THRESHOLD = 14;

export const useCfind5291TemplateTouchpoint = () => {
	const { isNewUser, loading: isNewUserLoading } = useIsNewUser({
		activeDaysThreshold: FIRST_TWO_WEEKS_THRESHOLD,
	});

	// Only fetch onboarding state when isNewUser is indeterminate (not a boolean)
	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP],
		!isNewUserLoading && typeof isNewUser === 'boolean',
	);
	const { nthUserOnboardingCompletedTimestamp } = deserializeState(onboardingStateData);

	// newUser trait can have a latency leading to an indeterminate value,
	// as a fallback we check if the user onboarded within the threshold based on the onboarding completed timestamp
	const completedTimestamp = Number(nthUserOnboardingCompletedTimestamp);
	const isUserOnboardedRecently =
		Boolean(completedTimestamp) &&
		differenceInDays(new Date(), new Date(completedTimestamp)) <= FIRST_TWO_WEEKS_THRESHOLD;

	// If still loading, don't enable yet
	if (isNewUserLoading || isOnboardingStateLoading) {
		return { isEnabled: false, loading: true };
	}

	// If isNewUser resolved to a definitive boolean, use it directly
	if (typeof isNewUser === 'boolean') {
		if (!isNewUser) {
			return { isEnabled: false, loading: false };
		}
		return {
			isEnabled: expVal('cfind-5291-template-touchpoint', 'isEnabled', false),
			loading: false,
		};
	}

	// Fallback: isNewUser is indeterminate, use onboarding timestamp
	if (!isUserOnboardedRecently) {
		return { isEnabled: false, loading: false };
	}

	return {
		isEnabled: expVal('cfind-5291-template-touchpoint', 'isEnabled', false),
		loading: false,
	};
};
